<template>
  <div class="mt-4">
    <v-layout>
      <v-flex>
        <v-btn flat @click="handleGoBack">
          <em class="fas fa-chevron-left mr-1" />
          {{ $t('Back') }}
        </v-btn>
      </v-flex>
      <v-flex v-if="list && list.length > 0" class="text-xs-right">
        <v-btn color="primary" @click="showExportGroup = true">{{ $t('export') }}</v-btn>
      </v-flex>
    </v-layout>

    <v-container>
      <h3 class="headline">{{ $t('Broadcast Detail') }}</h3>
    </v-container>

    <v-container v-if="broadcast">
      <div>{{ $t('Broadcast Name') }}</div>
      <div class="primary--text">{{ broadcast.delivery_tag }}</div>
    </v-container>
    <v-divider></v-divider>

    <template v-if="broadcast && ['sent'].includes(broadcast.status)">
      <v-tabs v-model="view" slider-color="primary">
        <v-tab href="#main">LON broadcast</v-tab>
        <v-tab href="#event-tracking">Event tracking</v-tab>
      </v-tabs>
      <div class="my-3">
        <div v-if="view === 'main'">
          <v-data-table
            :headers="headers"
            :items="list"
            class="elevation-1"
            hide-actions
            style="width: 100%"
          >
            <template #items="props">
              <td>{{ props.item.user_id || '-' }}</td>
              <td>
                {{
                  [props.item.first_name || '', props.item.last_name || ''].join(' ').trim() || '-'
                }}
              </td>
              <td>{{ props.item.phone_number || '-' }}</td>
              <td>
                {{ props.item.lon_status ? $t(`status_type.${props.item.lon_status}`) : '-' }}
              </td>
              <td v-if="isMyAllianz">
                {{ props.item.sms_status ? $t(`status_type.${props.item.sms_status}`) : '-' }}
              </td>
              <td v-if="props.item.lon_status === 'success' && props.item.log_timestamp">
                {{ props.item.log_timestamp | DateFormat('DD/MM/YYYY HH:mm:ss') }}
              </td>
              <td v-else>-</td>
            </template>
          </v-data-table>
          <div style="display: flex; justify-content: flex-end" class="mt-2">
            <v-pagination v-model="page" :length="pageLength" :total-visible="7" />
          </div>
        </div>
        <EventTracking
          v-if="view === 'event-tracking' && broadcast"
          :broadcast-id="Number(broadcast.id)"
          :tracking-url-ids="broadcast.link_event_tracking_ids || []"
          :filter.sync="eventTrackingFilter"
        />
      </div>
      <v-divider></v-divider>
    </template>

    <v-container>
      <div class="text-xs-center mt-2">
        <v-btn color="grey" outline @click="handleGoBack">
          <em class="fas fa-chevron-left mr-1" />
          {{ $t('Back') }}
        </v-btn>
      </div>
    </v-container>
    <template v-if="broadcast">
      <ExportGroup
        v-model="showExportGroup"
        :total="total"
        :filename="`${broadcast.delivery_tag}-report`"
        :limit="1000"
        @onExport="handleExport"
      ></ExportGroup>
    </template>
  </div>
</template>
<script>
import lineOfficialNotificationApi from '@/api/line-official-notification'
import { mapGetters } from 'vuex'
import ExportGroup from '@/components/elements/ExportGroup.vue'
import { PAGE_GROUP } from '@/constants/page'
import dayjs from 'dayjs'
import EventTracking from './EventTracking'
import * as XLSX from 'xlsx'
import lineBotGroupApi from '@/api/line-bot-group'
import eventTrackingApi from '@/api/event-tracking'

export default {
  components: {
    ExportGroup,
    EventTracking
  },
  data() {
    return {
      broadcast: null,
      page: 1,
      limit: 10,
      list: [],
      total: 0,
      showExportGroup: false,
      view: 'main',
      eventTrackingFilter: {
        from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        to: dayjs().format('YYYY-MM-DD'),
        pagination: {
          page: 1,
          rowsPerPage: 10
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    }),
    isMyAllianz() {
      return this.selectedBot.page_group === PAGE_GROUP.MY_ALLIANZ
    },
    headers() {
      const returnedItems = [
        { text: this.$t('user_id'), value: 'user_id', sortable: false },
        { text: this.$t('name'), value: 'name', sortable: false },
        { text: this.$t('mobile'), value: 'phone_number', sortable: false },
        { text: this.$t('Status'), value: 'status', sortable: false }
      ]
      if (this.isMyAllianz) {
        returnedItems.push({ text: this.$t('sms_status'), value: 'sms_status', sortable: false })
      }
      returnedItems.push({ text: 'Derive', value: 'timestamp', sortable: false })
      return returnedItems
    },
    pageLength() {
      if (this.total) {
        return Math.ceil(this.total / this.limit)
      } else {
        return 1
      }
    }
  },
  watch: {
    broadcast(val) {
      if (!val) return
      this.handleFetchDataLogStatus(val)
    },
    page(newVal, oldVal) {
      if (newVal != oldVal) {
        this.handleFetchDataLogStatus(this.broadcast)
      }
    }
  },
  methods: {
    handleGoBack() {
      this.$emit('go-back')
    },
    async handleFillForm(value) {
      const response = await lineOfficialNotificationApi.getLogLineOfficialNotificationBroadcast({
        params: {
          page_id: value.page_id,
          id: value.id
        }
      })

      this.broadcast = response.data
    },
    async handleFetchDataLogStatus(broadcast) {
      try {
        if (!broadcast || !['sent'].includes(broadcast.status)) return
        const response =
          await lineOfficialNotificationApi.getLogLineOfficialNotificationBroadcastLogs({
            params: {
              page_id: this.selectedBot.pageid,
              id: broadcast.id,
              limit: this.limit,
              offset: this.page * this.limit - this.limit
            }
          })
        const { status, data } = response
        if ([200].includes(status)) {
          this.list = []
          this.list = data.data || []
          this.total = data.total || 0
        }
      } catch (error) {
        console.error(error)
      }
    },
    async getExportData(limit, offset, isExport = false) {
      if (!this.broadcast.id) return
      const response =
        await lineOfficialNotificationApi.getLogLineOfficialNotificationBroadcastLogs({
          params: {
            page_id: this.selectedBot.pageid,
            id: this.broadcast.id,
            limit: limit,
            offset: offset
          }
        })
      const { status, data } = response
      if ([200].includes(status) && isExport) {
        const dataExport = data.data || []
        return dataExport.map((item) => {
          const userId = item.user_id || '-'
          const name = [item.first_name || '', item.last_name || ''].join(' ').trim() || '-'
          const phoneNumber = item.phone_number || '-'
          const status = item.lon_status || '-'
          const returned = [userId, name, phoneNumber, status]
          if (this.isMyAllianz) {
            returned.push(item.sms_status || '-')
          }
          returned.push(
            item.lon_status === 'success' && item.log_timestamp
              ? dayjs(item.log_timestamp).format('DD/MM/YYYY HH:mm:ss')
              : '-'
          )
          return returned
        })
      }
    },
    async handleExport({ limit, offset }) {
      const createBroadcastSheet = async () => {
        const formattedData = await this.getExportData(limit, offset, true)

        const createHeaders = () => {
          const returned = ['User ID', 'Name', 'Phone Number', 'Status']
          if (this.isMyAllianz) {
            returned.push('SMS Status')
          }
          returned.push('Derive')
          return returned
        }

        const rows = [
          [`Broadcast ID: ${this.broadcast.id}`],
          [`Broadcast Name: ${this.broadcast.delivery_tag}`],
          [],
          createHeaders(),
          ...formattedData
        ]

        const worksheet = XLSX.utils.aoa_to_sheet(rows)
        return worksheet
      }

      const createEventTrackingSheet = async () => {
        const fetchLineGroups = async () => {
          try {
            const response = await lineBotGroupApi.getLiveChatGroups({
              pageId: this.selectedBot.pageid,
              params: {
                limit: 1000,
                offset: 0
              }
            })
            const { data } = response.data
            return data
          } catch (error) {
            console.error(error)
          }
        }

        const fetchResult = async () => {
          try {
            const response = await eventTrackingApi.getLonAnalyticsDetail(this.selectedBot.pageid, {
              tracking_url_ids: this.broadcast.link_event_tracking_ids || [],
              lon_broadcast_id: Number(this.broadcast.id),
              limit: 65535,
              offset: 0,
              date_from: dayjs(this.eventTrackingFilter.from).startOf('day').valueOf(),
              date_to: dayjs(this.eventTrackingFilter.to).endOf('day').valueOf()
            })
            const { data, total } = response.data
            return {
              items: data,
              total: total
            }
          } catch (error) {
            console.error(error)
          }
        }

        const lineGroups = await fetchLineGroups()
        const { items } = await fetchResult()

        const groupName = (groupId) => {
          const group = lineGroups.find((group) => group.group_id === groupId)
          return group ? group.group_name : undefined
        }

        const headers = [
          'User ID',
          'Social name',
          'Group ID',
          'Group name',
          'Event channel',
          'Event value',
          'URL',
          'Timestamp'
        ]

        const mappedItems = items.map((item) => [
          item.user_id,
          item.user_social_name || [item.user.first_name, item.user.last_name].join(' ').trim(),
          item.user_group_id,
          groupName(item.user_group_id),
          item.channel,
          item.value,
          item.tracking_url,
          dayjs(item.created_at).format('DD/MM/YYYY HH:mm')
        ])

        const rows = [
          [`Broadcast ID: ${this.broadcast.id}`],
          [`Broadcast Name: ${this.broadcast.delivery_tag}`],
          [],
          headers,
          ...mappedItems
        ]

        const worksheet = XLSX.utils.aoa_to_sheet(rows)
        return worksheet
      }

      this.$store.dispatch('loading')

      const [broadcastSheet, eventTrackingSheet] = await Promise.all([
        createBroadcastSheet(),
        createEventTrackingSheet()
      ])

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, broadcastSheet, 'Broadcast')
      XLSX.utils.book_append_sheet(workbook, eventTrackingSheet, 'Event tracking')
      XLSX.writeFile(workbook, `${this.broadcast.delivery_tag}-report.xlsx`)

      this.$store.dispatch('clear_loading')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/LonBroadcast/LonBroadcastView.vue